import {
    Button,
    ButtonGroup,
    Center,
    Divider,
    Flex,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { BiMove, BiUserCircle } from 'react-icons/bi';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { validate } from 'uuid';

import { useDashboardWidgetState, useUpdateDashboardWidgetState } from '../api/dashboardWidget';
import { widgetSortFn } from './Dashboard';

export const WidgetOrderModal = ({
    onClose,
    allWidgets,
}: {
    onClose: () => void;
    allWidgets: { id: string; title: string }[];
}) => {
    const [hiddenWidgets, setHiddenWidgets] = useState<string[]>([]);
    const [widgetOrder, setWidgetOrder] = useState<string[]>([]);

    const { data: widgetState } = useDashboardWidgetState();
    const { mutate: save } = useUpdateDashboardWidgetState();

    useEffect(() => {
        if (!widgetState) {
            return;
        }

        // ensure we don't show old/removed widget on this modal
        const hidden = widgetState.hiddenWidgets.filter((id) => !!allWidgets.find((w) => w.id === id));
        setHiddenWidgets(hidden);

        const order = widgetState.widgetOrder.filter((id) => !!allWidgets.find((w) => w.id === id));
        const visible = allWidgets
            .filter((w) => !hidden.includes(w.id))
            .map((w) => w.id)
            .sort((a, b) => widgetSortFn(a, b, order));
        setWidgetOrder(visible);
    }, [allWidgets, widgetState]);

    const moveWidget = useCallback(
        (fromIdx: number, toIdx: number) => {
            const items = [...widgetOrder];
            const [reorderedItem] = items.splice(fromIdx, 1);
            items.splice(toIdx, 0, reorderedItem);
            setWidgetOrder(items);
        },
        [widgetOrder],
    );

    const hideWidget = useCallback((id: string) => {
        setHiddenWidgets((current) => [...current, id]);
        setWidgetOrder((current) => current.filter((w) => w !== id));
    }, []);

    const showWidget = useCallback((id: string) => {
        setHiddenWidgets((current) => current.filter((w) => w !== id));
        setWidgetOrder((current) => [...current, id]);
    }, []);

    const onSave = useCallback(() => {
        save(
            { hiddenWidgets, widgetOrder },
            {
                onSuccess: () => onClose(),
            },
        );
    }, [hiddenWidgets, onClose, save, widgetOrder]);

    return (
        <Modal isOpen onClose={onClose} scrollBehavior="inside" size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Widget order</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <DragDropContext
                        onDragEnd={(result) => {
                            if (result.destination) {
                                moveWidget(result.source.index, result.destination.index);
                            }
                        }}
                    >
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {widgetOrder.map((widgetId, idx) => {
                                        const widget = allWidgets.find((w) => w.id === widgetId);
                                        if (!widget) {
                                            return null;
                                        }
                                        return (
                                            <Draggable key={widgetId} draggableId={widgetId} index={idx}>
                                                {(prov) => (
                                                    <Flex
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={1}
                                                        px={2}
                                                        py={1}
                                                        borderWidth={1}
                                                        borderRadius="md"
                                                        gap={3}
                                                        ref={prov.innerRef}
                                                        {...prov.draggableProps}
                                                    >
                                                        <Flex gap={4}>
                                                            <Center {...prov.dragHandleProps}>
                                                                <Icon as={BiMove} />
                                                            </Center>
                                                            <Flex alignItems="center" gap={1}>
                                                                <Text>{widget.title}</Text>
                                                                {validate(widgetId) && (
                                                                    <Icon as={BiUserCircle} color="gray.500" />
                                                                )}
                                                            </Flex>
                                                        </Flex>
                                                        <IconButton
                                                            size="sm"
                                                            icon={<Icon as={BsEye} />}
                                                            aria-label="hide widget"
                                                            onClick={() => hideWidget(widgetId)}
                                                        />
                                                    </Flex>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder as React.ReactNode}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {!!hiddenWidgets.length && <Divider my={4} />}
                    {hiddenWidgets.map((widgetId) => {
                        const widget = allWidgets.find((w) => w.id === widgetId);
                        if (!widget) {
                            return null;
                        }
                        return (
                            <Flex
                                key={widget.id}
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                                px={2}
                                py={1}
                                borderWidth={1}
                                borderRadius="md"
                                gap={3}
                            >
                                <Flex ml={8} alignItems="center" gap={1}>
                                    <Text>{widget.title}</Text>
                                    {validate(widget.id) && <Icon as={BiUserCircle} color="gray.500" />}
                                </Flex>
                                <IconButton
                                    size="sm"
                                    icon={<Icon as={BsEyeSlash} color="red.300" />}
                                    aria-label="show widget"
                                    onClick={() => showWidget(widget.id)}
                                />
                            </Flex>
                        );
                    })}
                </ModalBody>

                <ModalFooter>
                    <ButtonGroup>
                        <Button colorScheme="teal" onClick={onSave}>
                            Save
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
